import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Users, UserPlus, Search, Loader2, Inbox, Mail, Phone,
    ChevronLeft, ChevronRight, Filter, X,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { adminMembersApi } from "@/lib/api/adminMembers";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const TIER_COLORS = {
    SILVER:   "bg-zinc-100 text-zinc-700 border-zinc-200",
    GOLD:     "bg-amber-50 text-amber-700 border-amber-200",
    PLATINUM: "bg-sky-50 text-sky-700 border-sky-200",
};

const STATUS_BADGES = {
    PENDING_PAYMENT: { en: "Awaiting payment",  ar: "بانتظار الدفع",   cls: "bg-amber-50 text-amber-700 border-amber-200" },
    PENDING_REVIEW:  { en: "Pending review",    ar: "قيد المراجعة",   cls: "bg-blue-50 text-blue-700 border-blue-200" },
    ACTIVE:          { en: "Active",            ar: "نشط",            cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const STATUS_FILTERS = [
    { value: "ALL",             en: "All",             ar: "الكل" },
    { value: "ACTIVE",          en: "Active",          ar: "نشط" },
    { value: "PENDING_REVIEW",  en: "Pending review",  ar: "قيد المراجعة" },
    { value: "PENDING_PAYMENT", en: "Awaiting payment",ar: "بانتظار الدفع" },
];

const PAGE_SIZE = 20;

function fmtDate(iso, lang) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(lang === "ar" ? "ar-EG" : "en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        });
    } catch (_) { return String(iso).slice(0, 10); }
}

export default function KioskMembersPage() {
    const { lang } = useI18n();
    const tr = (en, ar) => lang === "ar" ? ar : en;

    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [status, setStatus] = useState("ALL");

    // Debounce search input by 300ms
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Reset to page 1 on filter change
    useEffect(() => { setPage(1); }, [debouncedSearch, status]);

    useEffect(() => {
        const params = {
            limit: PAGE_SIZE,
            skip: (page - 1) * PAGE_SIZE,
            sort: "created_desc",
        };
        if (debouncedSearch) params.q = debouncedSearch;
        if (status !== "ALL") params.activation_status = status;

        setLoading(true);
        adminMembersApi.list(params)
            .then((res) => {
                setItems(res.items || []);
                setTotal(res.total || 0);
            })
            .catch((err) => {
                toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
                setItems([]);
            })
            .finally(() => setLoading(false));
    }, [page, debouncedSearch, status]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const showingStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const showingEnd = Math.min(page * PAGE_SIZE, total);

    return (
        <div className="max-w-4xl space-y-6 fade-up" data-testid="page-kiosk-members">
            {/* Header */}
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-sm font-medium text-primary">{tr("Sales Kiosk", "كشك المبيعات")}</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight inline-flex items-center gap-2">
                        <Users className="h-7 w-7 text-primary" />
                        {tr("My members", "أعضائي")}
                    </h1>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                        {tr(
                            "Everyone you've signed up. Search by name, email, phone or member code.",
                            "كل من سجلتهم. ابحث بالاسم أو البريد أو الهاتف أو كود العضوية."
                        )}
                    </p>
                </div>
                <Link
                    to="/kiosk/register"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
                >
                    <UserPlus className="h-4 w-4" />
                    {tr("Register member", "تسجيل عضو")}
                </Link>
            </header>

            {/* Search + status filter */}
            <div className="space-y-3">
                <div className="relative">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={tr("Search name, email, phone or code…", "ابحث بالاسم أو البريد أو الهاتف أو الكود…")}
                        className="w-full ps-10 pe-9 py-2.5 rounded-xl border border-border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                        data-testid="kiosk-members-search"
                    />
                    {search && (
                        <button
                            onClick={() => setSearch("")}
                            className="absolute end-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary"
                            aria-label="Clear"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1">
                    <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    {STATUS_FILTERS.map((f) => (
                        <button
                            key={f.value}
                            onClick={() => setStatus(f.value)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                                status === f.value
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-secondary text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid={`kiosk-members-filter-${f.value}`}
                        >
                            {lang === "ar" ? f.ar : f.en}
                        </button>
                    ))}
                </div>
            </div>

            {/* Result count */}
            {!loading && (
                <p className="text-xs text-muted-foreground">
                    {total === 0
                        ? tr("No matching members", "لا يوجد أعضاء")
                        : tr(
                            `Showing ${showingStart}–${showingEnd} of ${total}`,
                            `عرض ${showingStart}–${showingEnd} من ${total}`
                          )}
                </p>
            )}

            {/* List */}
            {loading && items === null ? (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            ) : items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border bg-card p-12 flex flex-col items-center gap-3 text-center">
                    <Inbox className="h-10 w-10 text-muted-foreground" />
                    <div>
                        <p className="text-sm font-medium text-foreground">
                            {debouncedSearch || status !== "ALL"
                                ? tr("No members match these filters", "لا يوجد أعضاء بهذه المعايير")
                                : tr("No members yet", "لم تسجل أي عضو بعد")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            {tr(
                                "Register your first member to start earning commission.",
                                "سجّل أول عضو لتبدأ في كسب العمولة."
                            )}
                        </p>
                    </div>
                    <Link
                        to="/kiosk/register"
                        className="mt-2 inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
                    >
                        <UserPlus className="h-3.5 w-3.5" />
                        {tr("Register a member", "تسجيل عضو")}
                    </Link>
                </div>
            ) : (
                <div className="space-y-2">
                    {items.map((m) => {
                        const tierCls = TIER_COLORS[m.tier] || TIER_COLORS.SILVER;
                        const status = STATUS_BADGES[m.activation_status] || null;
                        return (
                            <div
                                key={m.id}
                                className="rounded-2xl border border-border bg-card p-4 flex items-start gap-4"
                                data-testid={`kiosk-member-row-${m.id}`}
                            >
                                <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <span className="text-sm font-bold text-primary">
                                        {(m.full_name || "?").charAt(0).toUpperCase()}
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-1">
                                        <div className="min-w-0">
                                            <h3 className="text-base font-semibold truncate">{m.full_name || "—"}</h3>
                                            <p className="text-xs text-muted-foreground font-mono">{m.member_code}</p>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${tierCls}`}>
                                                {m.tier}
                                            </span>
                                            {status && (
                                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${status.cls}`}>
                                                    {lang === "ar" ? status.ar : status.en}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                        {m.email && (
                                            <span className="inline-flex items-center gap-1 truncate">
                                                <Mail className="h-3 w-3 shrink-0" />
                                                {m.email}
                                            </span>
                                        )}
                                        {m.phone && (
                                            <span className="inline-flex items-center gap-1">
                                                <Phone className="h-3 w-3" />
                                                {m.phone}
                                            </span>
                                        )}
                                        <span>
                                            {tr("Registered", "مسجل")} {fmtDate(m.created_at, lang)}
                                        </span>
                                        {m.expiry_date && (
                                            <span>
                                                {tr("Expires", "ينتهي")} {fmtDate(m.expiry_date, lang)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between gap-3 pt-2">
                    <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1 || loading}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-secondary text-foreground text-xs font-semibold hover:bg-secondary/80 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft className="h-3.5 w-3.5" />
                        {tr("Previous", "السابق")}
                    </button>
                    <p className="text-xs text-muted-foreground">
                        {tr(`Page ${page} of ${totalPages}`, `صفحة ${page} من ${totalPages}`)}
                    </p>
                    <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages || loading}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-secondary text-foreground text-xs font-semibold hover:bg-secondary/80 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {tr("Next", "التالي")}
                        <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                </div>
            )}
        </div>
    );
}
